'use client';

import { StyledInput } from '@ui/Form';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useRouter } from 'next/router';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoSaveIndicator } from './AutoSaveIndicator';
import { SectionMeta, SettingsPanelContext } from './SettingsPanelContext';

interface Props {
    children: ReactNode;
    /** Base URL of the settings page, e.g. /club/xyz/invoices/settings.
     *  When provided, sections register as Cmd+K nav items and ?section= deep-links work. */
    baseUrl?: string;
}

export function SettingsPanelLayout({ children, baseUrl }: Props) {
    const { t } = useTranslation('settings');
    const router = useRouter();
    const prefersReduced = useReducedMotion();
    // Initialise directly from the URL param — no effect needed
    const initialSection = (router.query.section as string | undefined) ?? null;
    const [activeId, setActiveId] = useState<string | null>(initialSection);
    const [sections, setSections] = useState<SectionMeta[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const activatedRef = useRef(false);

    // Sync the active section from the URL once the router is ready. On a hard load / open-in-new-tab
    // `router.query` is empty until hydration, so the initial useState seed misses `?section=`; this
    // also handles shallow Cmd+K navigation between sections while already on the page.
    useEffect(() => {
        if (!router.isReady) return;
        const section = router.query.section as string | undefined;
        if (section) setActiveId(section);
    }, [router.isReady, router.query.section]);

    const register = useCallback(
        (meta: SectionMeta) => {
            setSections((prev) => {
                if (prev.find((s) => s.id === meta.id)) return prev;
                return [...prev, meta];
            });
            if (!activatedRef.current) {
                activatedRef.current = true;
                if (!router.query.section) setActiveId(meta.id);
            }
        },
        [router.query.section]
    );

    const unregister = useCallback((id: string) => {
        setSections((prev) => prev.filter((s) => s.id !== id));
    }, []);

    const activate = useCallback(
        (id: string) => {
            setActiveId(id);
            if (baseUrl) {
                router.replace(`${baseUrl}?section=${id}`, undefined, { shallow: true });
            }
        },
        [baseUrl, router]
    );

    const filteredSections = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return sections;
        return sections.filter(
            (s) =>
                s.title.toLowerCase().includes(q) ||
                s.keywords?.toLowerCase().includes(q) ||
                s.description?.toLowerCase().includes(q)
        );
    }, [sections, searchQuery]);

    const activeSection = sections.find((s) => s.id === activeId);

    const ctx = useMemo(
        () => ({ activeId, searchQuery, baseUrl: baseUrl ?? null, activate, register, unregister }),
        [activeId, searchQuery, baseUrl, activate, register, unregister]
    );

    return (
        <SettingsPanelContext.Provider value={ctx}>
            <div className="tw-flex tw-h-full tw-min-h-0 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white">
                {/* Left panel */}
                <div className="tw-flex tw-w-52 tw-shrink-0 tw-flex-col tw-border-0 tw-border-r tw-border-solid tw-border-accents-2">
                    {/* Search */}
                    <div className="tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-p-2">
                        <StyledInput
                            placeholder={t('panel.searchPlaceholder')}
                            aria-label={t('panel.searchAriaLabel')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            fullWidth
                            flush
                            bordered
                            size="xs"
                        />
                    </div>

                    {/* Section list */}
                    <div className="tw-flex-1 tw-overflow-y-auto tw-p-1.5">
                        {filteredSections.length === 0 && (
                            <p className="tw-px-3 tw-py-3 tw-text-center tw-text-xs tw-text-accents-7">
                                {t('panel.noResults')}
                            </p>
                        )}
                        {filteredSections.map((section) => (
                            <button
                                key={section.id}
                                type="button"
                                onClick={() => activate(section.id)}
                                aria-current={activeId === section.id ? 'page' : undefined}
                                className={`tw-mb-1 tw-flex tw-w-full tw-items-center tw-rounded-md tw-border-none tw-px-2.5 tw-py-2.5 tw-text-left tw-transition-colors last:tw-mb-0 ${
                                    activeId === section.id
                                        ? 'tw-bg-accents-2 tw-font-medium tw-text-accents-9'
                                        : 'tw-bg-transparent tw-text-accents-8 hover:tw-bg-accents-1 hover:tw-text-accents-9'
                                }`}
                            >
                                <span className="tw-truncate tw-text-xs" title={section.title}>
                                    {section.title}
                                </span>
                                {section.status === 'needs-setup' && (
                                    <span
                                        className="tw-ml-auto tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full tw-bg-warning"
                                        aria-label={t('panel.needsSetupAriaLabel')}
                                        role="img"
                                    />
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Auto-save notice — doubles as the save indicator, which is why
                        auto-saving no longer raises a toast on every pause in typing. */}
                    <AutoSaveIndicator className="tw-border-0 tw-border-t tw-border-solid tw-border-accents-2 tw-px-3 tw-py-2" />
                </div>

                {/* Right panel */}
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-p-6">
                    <AnimatePresence mode="wait">
                        {activeSection && (
                            <motion.div
                                key={activeSection.id}
                                initial={{ opacity: 0, y: prefersReduced ? 0 : 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: prefersReduced ? 0 : -4 }}
                                transition={{ duration: prefersReduced ? 0 : 0.13 }}
                                className="tw-mb-5 tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-pb-4"
                            >
                                <h2 className="tw-mb-1 tw-text-base tw-font-semibold tw-text-accents-9">
                                    {activeSection.title}
                                </h2>
                                {activeSection.description && (
                                    <p className="tw-m-0 tw-text-sm tw-text-accents-8">{activeSection.description}</p>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="tw-flex-1">{children}</div>
                </div>
            </div>
        </SettingsPanelContext.Provider>
    );
}
